'use client';
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { LoaderCircle, Sparkles, Send } from 'lucide-react';

interface BubbleProps {
  onClick: () => void;
  agentState: 'listening' | 'thinking' | 'speaking' | 'initializing';
  label?: string;
  loading?: boolean;
  onSubmit?: (text: string) => void;
  error?: string;
}

// Collapsed-state affordance: a translucent, blurred pill (sparkle icon +
// label + send arrow) rather than a plain circular avatar-letter bubble.
// Matches momentum.mobeus.com's actual idle "Let's talk" pill — computed
// styles pulled from the live site (background rgba(255,255,255,0.1),
// backdrop-filter: blur(4px), border 1px solid rgba(255,255,255,0.12),
// fully-rounded). Reuses the existing --mobeus-input-* tokens rather than
// inventing new ones, since it's visually the same glass-pill treatment as
// the text input in the expanded panel.
export default function Bubble({ onClick, agentState, label = "Let's talk", loading = false, onSubmit, error }: BubbleProps) {
  const iconColor =
    agentState === 'speaking' ? 'var(--mobeus-bubble-speaking, #10b981)'
    : agentState === 'thinking' ? 'var(--mobeus-bubble-thinking, #f59e0b)'
    : 'var(--mobeus-primary, #3b82f6)';
  const pillStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10,
    height: 52, padding: '0 10px 0 18px',
    borderRadius: 999,
    background: 'var(--mobeus-input-bg, rgba(255,255,255,0.1))',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    border: '1px solid var(--mobeus-input-border, rgba(255,255,255,0.12))',
    color: 'var(--mobeus-text, #111827)',
    cursor: loading ? 'wait' : 'pointer',
    opacity: loading ? 0.82 : 1,
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
    fontFamily: 'var(--mobeus-font, system-ui, sans-serif)',
    fontSize: 14,
  };
  const [text, setText] = useState('');
  if (onSubmit) {
    return (
      <form className="mobeus-bubble" part="bubble" style={{ ...pillStyle, width: 'min(430px, calc(100vw - 32px))', boxSizing: 'border-box', margin: 0, position: 'relative' }}
        aria-busy={loading || undefined}
        onSubmit={(event) => { event.preventDefault(); if (!loading) onSubmit(text.trim() || label); }}>
        <style>{`@keyframes mobeus-spin { to { transform: rotate(360deg); } } .mobeus-spinner { animation: mobeus-spin 0.8s linear infinite; } .mobeus-launcher-input::placeholder { color: inherit; opacity: 1; }`}</style>
        {loading ? <LoaderCircle className="mobeus-spinner" size={16} color={iconColor} aria-hidden="true" /> : <Sparkles size={16} color={iconColor} aria-hidden="true" />}
        {error && !loading && (
          <span role="alert" part="bubble-error" style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, right: 0,
            padding: '10px 14px', borderRadius: 12, background: 'var(--mobeus-input-bg, #111827)', color: 'inherit', cursor: 'default' }}>
            {error}
          </span>
        )}
        <input className="mobeus-launcher-input" part="bubble-input" aria-label="Ask a question" placeholder={loading ? 'Connecting…' : label}
          value={loading ? '' : text} onChange={(event) => setText(event.target.value)} disabled={loading} maxLength={4000}
          style={{ flex: 1, minWidth: 0, width: 0, border: 0, outline: 0, padding: 0, background: 'transparent', color: 'inherit', font: 'inherit' }} />
        <button type="submit" part="bubble-send" aria-label="Start conversation" disabled={loading}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            border: 0, padding: 0, cursor: loading ? 'wait' : 'pointer', background: 'var(--mobeus-primary, #3b82f6)', color: 'var(--mobeus-primary-text, #fff)' }}>
          <Send size={14} aria-hidden="true" />
        </button>
      </form>
    );
  }
  return (
    <>
      <style>{`
        @keyframes mobeus-spin { to { transform: rotate(360deg); } }
        .mobeus-spinner { animation: mobeus-spin 0.8s linear infinite; }
      `}</style>
      <button
        className="mobeus-bubble" part="bubble"
        onClick={loading ? undefined : onClick}
        disabled={loading}
        aria-busy={loading ? 'true' : undefined}
        aria-label={loading ? 'Starting Mobeus assistant' : 'Open Mobeus assistant'}
        style={pillStyle}
      >
        {loading ? (
          <LoaderCircle className="mobeus-spinner" size={16} color={iconColor} aria-hidden="true" />
        ) : (
          <Sparkles size={16} color={iconColor} aria-hidden="true" />
        )}
        <span>{label}</span>
        <span
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: 'var(--mobeus-primary, #3b82f6)', color: 'var(--mobeus-primary-text, #fff)',
          }}
        >
          <Send size={14} aria-hidden="true" />
        </span>
      </button>
    </>
  );
}
